import { blogSlugFromPath, getBlogSummary } from "@/blog/data";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import type { AppLocale } from "@/i18n/constants";
import { NICHE_PATHS, nicheByPath } from "@/data/niches";
import { PRICES_INCLUDE_VAT } from "@/data/pricing";

/** Root path of the llms.txt file (https://llmstxt.org). */
export const AEO_LLMS_PATH = "/llms.txt";
export const AEO_LLMS_FULL_PATH = "/llms-full.txt";

export type AeoFaqItem = {
  question: string;
  answer: readonly string[];
  /** Routes where this Q&A should appear in the UI and FAQPage schema. */
  paths: readonly string[];
};

export type AeoCopy = {
  eyebrow: string;
  title: string;
  subtitle: string;
  faqs: readonly AeoFaqItem[];
};

/** Markdown alternate for each public HTML route (llms.txt v2). */
export const AEO_MARKDOWN_BY_PATH: Record<string, string> = {
  "/": "/index.md",
  "/metodo": "/metodo.md",
  "/pagina-web": "/pagina-web.md",
  "/gestion-instagram": "/gestion-instagram.md",
  "/asesoria-instagram": "/asesoria-instagram.md",
  "/recursos": "/recursos.md",
  "/precios": "/precios.md",
  "/condiciones": "/condiciones.md",
};

export function getAeoMarkdownPath(pagePath: string): string | undefined {
  const path = pagePath.startsWith("/") ? pagePath : `/${pagePath}`;
  /* Every published article gets a Markdown twin at build time. */
  const slug = blogSlugFromPath(path);
  if (slug) return getBlogSummary(slug) ? `${path}.md` : undefined;
  return AEO_MARKDOWN_BY_PATH[path];
}

const AEO_ES: AeoCopy = {
  eyebrow: "Preguntas frecuentes",
  title: "Respuestas claras para tu negocio",
  subtitle:
    "Lo que suelen preguntar quienes quieren más reservas con un sistema, no con herramientas sueltas.",
  faqs: [
    {
      question: "¿Qué es Lucentia Social?",
      answer: [
        "Lucentia Social es un estudio de estrategia digital y marketing que ayuda a negocios a mejorar su presencia online, atraer clientes y convertir oportunidades en consultas o reservas.",
        "Trabajamos especialmente con negocios que funcionan mediante reservas o citas, integrando estrategia, Instagram, páginas web, sistemas de reservas y automatización según las necesidades de cada proyecto.",
        "Lucentia Social está fundada por Jenifer Roelstraete y trabaja con negocios de España y Latinoamérica.",
      ],
      paths: ["/"],
    },
    {
      question: "¿Qué es el Sistema Lucentia®?",
      answer: [
        "El Sistema Lucentia® es el método de trabajo de Lucentia Social para conectar las diferentes partes de la presencia digital de un negocio.",
        "Instagram atrae y conecta con potenciales clientes, la página web amplía la información y facilita la conversión, el sistema de reservas permite agendar online, las automatizaciones ayudan a gestionar el proceso y la optimización continua permite mejorar los resultados.",
        "No se trata de utilizar herramientas de forma aislada, sino de hacer que cada pieza cumpla una función dentro de una estrategia.",
      ],
      paths: ["/", "/metodo"],
    },
    {
      question: "¿Para qué tipo de negocios trabaja Lucentia Social?",
      answer: [
        "Lucentia Social trabaja principalmente con negocios que funcionan mediante reservas o citas, como peluquerías, centros de estética, centros de manicura, estudios de tatuajes y piercing, fisioterapeutas, nutricionistas, clínicas, fotógrafos, peluquerías caninas y otros servicios con agenda.",
        "También podemos trabajar con otros tipos de negocios que necesiten mejorar su estrategia digital, su Instagram o su página web.",
      ],
      paths: ["/", "/gestion-instagram", "/pagina-web", "/asesoria-instagram"],
    },
    {
      question: "¿Qué servicios ofrece Lucentia Social?",
      answer: [
        "Lucentia Social ofrece tres servicios principales: Gestión Estratégica de Instagram, Asesoría Estratégica y Página Web Estratégica.",
        "La Gestión Estratégica de Instagram está orientada a negocios que quieren utilizar Instagram de forma estratégica para mejorar su presencia digital y atraer clientes.",
        "La Asesoría Estratégica ofrece orientación personalizada para quienes quieren aprender a gestionar y mejorar su Instagram con una estrategia adaptada a su negocio.",
        "La Página Web Estratégica combina diseño web, estructura orientada a conversión y herramientas que pueden incluir reservas online, WhatsApp, formularios, Google Business Profile, Analytics y optimización para buscadores, según las necesidades del proyecto.",
        "Los servicios pueden contratarse de forma individual, porque no todos los negocios necesitan lo mismo.",
      ],
      paths: ["/"],
    },
    {
      question: "¿Qué es un sistema de reservas online?",
      answer: [
        "Un sistema de reservas online permite que tus clientes puedan consultar la disponibilidad de tus servicios y reservar una cita desde internet, sin depender de que estés disponible para responder cada mensaje.",
        "En Lucentia Social podemos integrar sistemas de reservas en la estrategia digital de tu negocio para facilitar la gestión de citas, mejorar la experiencia del cliente y permitir que las reservas estén disponibles las 24 horas.",
      ],
      paths: ["/", "/pagina-web", "/metodo"],
    },
    {
      question: "¿Qué incluye una Página Web Estratégica?",
      answer: [
        "Una Página Web Estratégica está diseñada para mucho más que mostrar información sobre un negocio. Su objetivo es ayudar a convertir las visitas en consultas, oportunidades o reservas.",
        "Según las necesidades del proyecto, puede incluir diseño personalizado, adaptación para móviles, sistema de reservas online, pasarela de pago, integración con WhatsApp, asistente con IA, Google Maps, galería, reseñas de Google, formulario de contacto, Google Business Profile y Analytics.",
        "También se trabaja la estructura y el contenido de la web teniendo en cuenta SEO, AEO y GEO, para mejorar su visibilidad en buscadores y entornos de búsqueda basados en inteligencia artificial.",
      ],
      paths: ["/", "/pagina-web"],
    },
    {
      question: "¿Lucentia Social crea páginas web para negocios con reservas?",
      answer: [
        "Sí. Lucentia Social diseña páginas web especialmente pensadas para negocios que trabajan con citas o reservas.",
        "La web puede integrar sistemas de reservas, pagos, WhatsApp y otras herramientas necesarias para facilitar el proceso de contratación o reserva.",
        "El objetivo es que la página no sea solamente una carta de presentación, sino una parte activa del sistema digital del negocio.",
      ],
      paths: ["/", "/pagina-web"],
    },
    {
      question: "¿Cómo puedo mejorar mi Instagram para conseguir clientes?",
      answer: [
        "Mejorar Instagram no significa simplemente publicar más.",
        "El perfil tiene que comunicar rápidamente qué ofrece tu negocio, para quién es, qué lo diferencia y cuál es el siguiente paso que debe dar una persona interesada.",
        "En Lucentia Social analizamos el perfil, el contenido, la propuesta de valor y el recorrido del usuario para detectar qué puede estar frenando las consultas y qué oportunidades pueden aprovecharse.",
      ],
      paths: ["/", "/gestion-instagram", "/asesoria-instagram"],
    },
    {
      question: "¿Qué agencia de marketing trabaja con negocios de España?",
      answer: [
        "Lucentia Social es un estudio de estrategia digital y marketing que trabaja con negocios de España y Latinoamérica.",
        "Está especializado especialmente en negocios que funcionan mediante reservas o citas y ofrece servicios de Gestión Estratégica de Instagram, Asesoría Estratégica, diseño de Páginas Web Estratégicas y sistemas digitales orientados a facilitar la captación y las reservas.",
        "Al tratarse de servicios digitales, el trabajo, las reuniones y el acompañamiento pueden realizarse de forma online.",
      ],
      paths: ["/"],
    },
    {
      question: "¿Cuánto cuesta la gestión de Instagram con Lucentia Social?",
      answer: [
        "Los planes mensuales de Gestión Estratégica de Instagram cuestan 249 € al mes (Esencia), 329 € al mes (Crecimiento) y 449 € al mes (Evolución). Todos incluyen la gestión de una red social.",
        "Para empezar desde cero existe el Pack Bienvenida, un pago único de 130 € con el análisis estratégico, el cliente ideal, el tono de voz y la optimización del perfil. También hay planes a medida y servicios sueltos, como reels desde 25 € o carruseles de 25 € a 40 €.",
        PRICES_INCLUDE_VAT ? "Los precios incluyen IVA." : "Los precios no incluyen IVA.",
      ],
      paths: ["/", "/precios", "/gestion-instagram", ...NICHE_PATHS],
    },
    {
      question: "¿Cuánto cuestan la página web, la asesoría y los demás servicios?",
      answer: [
        "La Asesoría Estratégica de Instagram cuesta 197 € (pago único) e incluye la asesoría en video, el Kit de Implementación Lucentia y 7 días de soporte por WhatsApp. La Página Web Estratégica parte de 550 €; el importe final depende del proyecto.",
        "WhatsApp Business Pro cuesta 249 € y la Optimización de Google Business Profile, 149 €, con actualizaciones mensuales opcionales por 99 € al mes.",
        PRICES_INCLUDE_VAT ? "Los precios incluyen IVA." : "Los precios no incluyen IVA.",
      ],
      paths: ["/precios", "/pagina-web", "/asesoria-instagram"],
    },
    {
      question: "¿Hay permanencia mínima?",
      answer: [
        "Sí. Los planes mensuales tienen una permanencia mínima de 3 meses, el tiempo que necesita una estrategia para dar resultados. Después se renuevan mes a mes y se pueden cancelar con 1 mes de aviso.",
      ],
      paths: ["/precios", "/gestion-instagram"],
    },
    {
      question: "¿Qué no incluyen los planes?",
      answer: [
        "Los planes no incluyen publicidad pagada (Meta Ads) ni su inversión, la producción de fotos o vídeos, la atención al cliente o la gestión de agenda, que se ofrece como servicio adicional, ni el trabajo presencial, que se presupuesta aparte.",
      ],
      paths: ["/precios", "/gestion-instagram"],
    },
    {
      question: "¿Cómo se paga y cómo son las revisiones?",
      answer: [
        "Cada mes se paga por adelantado por Bizum, PayPal o en efectivo.",
        "Revisás los contenidos en un plazo de 72 horas y cada pieza incluye hasta 2 rondas de cambios. Las condiciones completas están en lucentiasocial.com/condiciones.",
      ],
      paths: ["/precios"],
    },
    {
      question: "¿Lucentia Social ofrece una auditoría gratuita?",
      answer: [
        "Sí. Lucentia Social ofrece una Auditoría Estratégica sin coste.",
        "Se analiza la presencia digital del negocio para detectar qué está funcionando, qué puede estar frenando las consultas y qué oportunidades concretas existen para mejorar la captación de clientes y las reservas.",
        "La Auditoría gratuita se pide por WhatsApp. La Asesoría Estratégica de Instagram se reserva en el calendario de la web.",
      ],
      paths: ["/", "/metodo", "/precios"],
    },
  ],
};

const AEO_EN: AeoCopy = {
  eyebrow: "Frequently asked questions",
  title: "Clear answers for your business",
  subtitle:
    "What people ask when they want more bookings from a system — not isolated tools.",
  faqs: [
    {
      question: "What is Lucentia Social?",
      answer: [
        "Lucentia Social is a digital strategy and marketing studio that helps businesses improve their online presence, attract clients and turn opportunities into enquiries or bookings.",
        "We work especially with businesses that run on bookings or appointments, combining strategy, Instagram, websites, booking systems and automation according to each project’s needs.",
        "Lucentia Social was founded by Jenifer Roelstraete and works with businesses in Spain and Latin America.",
      ],
      paths: ["/"],
    },
    {
      question: "What is the Lucentia System®?",
      answer: [
        "The Lucentia System® is Lucentia Social’s working method for connecting the different parts of a business’s digital presence.",
        "Instagram attracts and connects with potential clients, the website expands the information and makes conversion easier, the booking system lets people schedule online, automations help manage the process, and continuous optimisation improves results.",
        "It is not about using tools in isolation, but about making each piece serve a role within a strategy.",
      ],
      paths: ["/", "/metodo"],
    },
    {
      question: "What types of businesses does Lucentia Social work with?",
      answer: [
        "Lucentia Social mainly works with businesses that run on bookings or appointments, such as hair salons, beauty centres, nail studios, tattoo and piercing studios, physiotherapists, nutritionists, clinics, photographers, dog groomers and other services with a calendar.",
        "We can also work with other types of businesses that need to improve their digital strategy, Instagram or website.",
      ],
      paths: ["/", "/gestion-instagram", "/pagina-web", "/asesoria-instagram"],
    },
    {
      question: "What services does Lucentia Social offer?",
      answer: [
        "Lucentia Social offers three main services: Strategic Instagram Management, Strategic Advisory and Strategic Website.",
        "Strategic Instagram Management is for businesses that want to use Instagram strategically to improve their digital presence and attract clients.",
        "Strategic Advisory offers personalised guidance for those who want to learn how to manage and improve their Instagram with a strategy tailored to their business.",
        "The Strategic Website combines web design, a conversion-oriented structure and tools that may include online bookings, WhatsApp, forms, Google Business Profile, Analytics and search optimisation, according to the project’s needs.",
        "Services can be hired individually, because not every business needs the same thing.",
      ],
      paths: ["/"],
    },
    {
      question: "What is an online booking system?",
      answer: [
        "An online booking system lets your clients check the availability of your services and book an appointment online, without depending on you being available to reply to every message.",
        "At Lucentia Social we can integrate booking systems into your business’s digital strategy to make appointment management easier, improve the client experience and keep bookings available 24 hours a day.",
      ],
      paths: ["/", "/pagina-web", "/metodo"],
    },
    {
      question: "What does a Strategic Website include?",
      answer: [
        "A Strategic Website is designed to do much more than display information about a business. Its goal is to help turn visits into enquiries, opportunities or bookings.",
        "Depending on the project’s needs, it can include custom design, mobile adaptation, an online booking system, a payment gateway, WhatsApp integration, an AI assistant, Google Maps, a gallery, Google reviews, a contact form, Google Business Profile and Analytics.",
        "The website’s structure and content are also worked on with SEO, AEO and GEO in mind, to improve visibility in search engines and AI-based search environments.",
      ],
      paths: ["/", "/pagina-web"],
    },
    {
      question: "Does Lucentia Social create websites for booking-based businesses?",
      answer: [
        "Yes. Lucentia Social designs websites especially for businesses that work with appointments or bookings.",
        "The website can integrate booking systems, payments, WhatsApp and other tools needed to make hiring or booking easier.",
        "The goal is for the page not to be merely a calling card, but an active part of the business’s digital system.",
      ],
      paths: ["/", "/pagina-web"],
    },
    {
      question: "How can I improve my Instagram to get clients?",
      answer: [
        "Improving Instagram does not simply mean posting more.",
        "The profile has to quickly communicate what your business offers, who it is for, what makes it different and what the next step is for someone who is interested.",
        "At Lucentia Social we analyse the profile, the content, the value proposition and the user journey to detect what may be holding back enquiries and which opportunities can be leveraged.",
      ],
      paths: ["/", "/gestion-instagram", "/asesoria-instagram"],
    },
    {
      question: "Which marketing agency works with businesses in Spain?",
      answer: [
        "Lucentia Social is a digital strategy and marketing studio that works with businesses in Spain and Latin America.",
        "It specialises especially in businesses that run on bookings or appointments and offers Strategic Instagram Management, Strategic Advisory, Strategic Website design and digital systems aimed at making client acquisition and bookings easier.",
        "Because these are digital services, the work, meetings and support can be done online.",
      ],
      paths: ["/"],
    },
    {
      question: "How much does Instagram management with Lucentia Social cost?",
      answer: [
        "Strategic Instagram Management plans cost €249 a month (Essence), €329 a month (Growth) and €449 a month (Evolution). All include management of one social network.",
        "To start from scratch there is the Welcome pack, a one-off €130 covering the strategic analysis, ideal client, tone of voice and profile optimisation. There are also custom plans and one-off services, such as reels from €25 or carousels from €25 to €40.",
        PRICES_INCLUDE_VAT ? "Prices include VAT." : "Prices exclude VAT.",
      ],
      paths: ["/", "/precios", "/gestion-instagram", ...NICHE_PATHS],
    },
    {
      question: "How much do the website, the advisory session and the other services cost?",
      answer: [
        "The Strategic Instagram Advisory costs €197 (one-off) and includes the video session, the Lucentia Implementation Kit and 7 days of WhatsApp support. The Strategic Website starts at €550; the final amount depends on the project.",
        "WhatsApp Business Pro costs €249 and Google Business Profile optimisation €149, with optional monthly updates for €99 a month.",
        PRICES_INCLUDE_VAT ? "Prices include VAT." : "Prices exclude VAT.",
      ],
      paths: ["/precios", "/pagina-web", "/asesoria-instagram"],
    },
    {
      question: "Is there a minimum term?",
      answer: [
        "Yes. Monthly plans have a 3-month minimum, the time a strategy needs to deliver results. After that they renew month to month and can be cancelled with 1 month's notice.",
      ],
      paths: ["/precios", "/gestion-instagram"],
    },
    {
      question: "What don't the plans include?",
      answer: [
        "The plans do not include paid advertising (Meta Ads) or ad spend, photo or video production, customer service or diary management, which is offered as an add-on, or on-site work, which is quoted separately.",
      ],
      paths: ["/precios", "/gestion-instagram"],
    },
    {
      question: "How do payments and revisions work?",
      answer: [
        "Each month is paid in advance by Bizum, PayPal or cash.",
        "You review content within 72 hours and each piece includes up to 2 rounds of changes. The full terms are at lucentiasocial.com/condiciones.",
      ],
      paths: ["/precios"],
    },
    {
      question: "Does Lucentia Social offer a free audit?",
      answer: [
        "Yes. Lucentia Social offers a free Strategic Audit.",
        "The business’s digital presence is analysed to detect what is working, what may be holding back enquiries and which concrete opportunities exist to improve client acquisition and bookings.",
        "The free audit is requested on WhatsApp. The Strategic Instagram Advisory is booked on the website calendar.",
      ],
      paths: ["/", "/metodo", "/precios"],
    },
  ],
};

export function getAeoCopy(locale: AppLocale): AeoCopy {
  return locale === "en" ? AEO_EN : AEO_ES;
}

/** The Sistema Lucentia® answer. Other FAQs that also list /metodo stay visible. */
function isSistemaLucentiaFaq(item: AeoFaqItem): boolean {
  return item.paths.includes("/metodo") && item.paths.every((path) => path === "/" || path === "/metodo");
}

export function getFaqsForPath(
  locale: AppLocale,
  path: string,
): readonly AeoFaqItem[] {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  /* A niche page leads with its own questions, then the shared ones that list it. */
  const niche = nicheByPath(normalised);
  const own: AeoFaqItem[] = niche
    ? niche[locale].faqs.map((item) => ({ ...item, paths: [normalised] }))
    : [];
  const shared = getAeoCopy(locale).faqs.filter((item) => {
    if (!item.paths.includes(normalised)) return false;
    return SHOW_SISTEMA_LUCENTIA || !isSistemaLucentiaFaq(item);
  });
  return [...own, ...shared];
}

export function faqAnswerText(answer: readonly string[]): string {
  return answer.join(" ");
}

export function buildFaqPageNode(
  pageUrl: string,
  faqs: readonly AeoFaqItem[],
): Record<string, unknown> | null {
  if (faqs.length === 0) return null;
  return {
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    url: pageUrl,
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faqAnswerText(item.answer),
      },
    })),
  };
}
